import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import { riskOptions } from "./RiskAppetiteModal";

export interface FiltersFieldProps {
  sector: string[];
  near200SMA: string[];
  fibLevel: string[];
  segment: string[];
  stockTrend: string[];
  machineLogic: string;
  propLogic: string;
  hotFlame?: boolean;
  orderBlock?: boolean;
  customUniverse?: boolean;
  snowFlake?: boolean;
  abovesma20?: boolean;
  highvolume?: boolean;
  qualityStocks?: boolean;
  risk_profile: string[];
}

interface FilterOption {
  label: string;
  value: string;
}

type FiltersData = Record<string, FilterOption[]>;

interface FiltersProps {
  toggleFilterModal: () => void;
  resetAllFilters: (date: string) => void;
  applyFilters: (date: string) => void;
  setSelectedFilters: React.Dispatch<React.SetStateAction<FiltersFieldProps>>;
  selectedFilters: FiltersFieldProps;
  selectedDate: string;
  filtersData: FiltersData;
  appliedFilters: FiltersFieldProps;
  isPerformancePage?: boolean;
}

/* --------------------- MultiSelect (Inline, no portal) --------------------- */

interface MultiSelectProps {
  label: string;
  options: FilterOption[];
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  selecteAll?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  values,
  onChange,
  placeholder,
  selecteAll,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(
    () =>
      options.filter((o) =>
        o.label.toLowerCase().includes(search.trim().toLowerCase()),
      ),
    [options, search],
  );

  const allSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((o) => values.includes(o.value));

  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((s) => !s);

  // handle outside clicks + escape
  useEffect(() => {
    if (!isOpen) return;
    const onClick = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        menuRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        !menuRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);

  const toggleOption = (value: string) => {
    if (values.includes(value)) {
      onChange(values.filter((v) => v !== value));
    } else {
      onChange([...values, value]);
    }
  };
  const toggleSelectAll = () => {
    const filteredValues = filteredOptions.map((o) => o.value);
    const allFilteredSelected = filteredValues.every((v) => values.includes(v));
    if (allFilteredSelected) {
      onChange(values.filter((v) => !filteredValues.includes(v)));
    } else {
      onChange([...new Set([...values, ...filteredValues])]);
    }
  };

  return (
    <div className="w-full relative">
      <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
        {label}
      </label>

      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={toggle}
        aria-expanded={isOpen}
        className="w-full bg-white dark:bg-black pl-4 pr-3 py-2 border border-[#a0a5bb] rounded-md cursor-pointer text-sm dark:text-white truncate relative flex items-center justify-between"
      >
        <span>
          {values.length > 0
            ? `${values.length} selected`
            : placeholder || `Select ${label}`}
        </span>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={menuRef}
          className="absolute mt-1 bg-white dark:bg-[#1E1E2D] border border-gray-300 dark:border-gray-700 rounded-md shadow-xl overflow-y-auto z-[1000]"
          style={{ maxHeight: 182, width: "100%" }}
        >
          {/* Search */}
          <div className="p-2 sticky top-0 bg-white dark:bg-[#1E1E2D] border-b border-gray-200 dark:border-gray-700">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-2 py-1 border rounded-md text-sm dark:bg-black dark:text-white"
            />
          </div>

          {/* Select All */}
          {!selecteAll && (
            <button
              type="button"
              onClick={toggleSelectAll}
              className="w-full flex items-center cursor-pointer justify-start gap-2 text-gray-700 dark:text-white px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <input type="checkbox" checked={allSelected} readOnly />
              <span className="text-sm">Select All</span>
            </button>
          )}

          {/* Options */}
          {filteredOptions.map((opt) => {
            const checked = values.includes(opt.value);
            return (
              <button
                type="button"
                key={opt.value}
                onClick={() => toggleOption(opt.value)}
                className="w-full flex items-center cursor-pointer justify-start gap-2 text-gray-700 dark:text-white px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <input type="checkbox" checked={checked} readOnly />
                <span className="text-sm">{opt.label}</span>
              </button>
            );
          })}
          {filteredOptions.length === 0 && (
            <div className="p-3 text-sm text-gray-500">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

/* -------------------------- Main Filters UI -------------------------- */

const Filters = ({
  toggleFilterModal,
  resetAllFilters,
  applyFilters,
  setSelectedFilters,
  selectedFilters,
  selectedDate,
  filtersData,
  appliedFilters,
  isPerformancePage,
}: FiltersProps) => {
  const user = getLocalStorageUser();
  const hasCustomUniverseAccess =
    user?.plan === process.env.NEXT_PUBLIC_REQUIRED_PLAN;
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      if (JSON.stringify(selectedFilters) === JSON.stringify(appliedFilters)) {
        toggleFilterModal();
        setSelectedFilters(appliedFilters);
      }
    }
  };

  return (
    <div
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm p-2 sm:p-4 bg-black/40"
    >
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] sm:max-h-[90vh] flex flex-col border border-gray-200 dark:border-gray-700 overflow-hidden font-sans">
        {/* Header */}
        <div className="flex items-center justify-between px-3.5 py-2.5 sm:px-5 sm:py-3.5 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white leading-tight">
              Advanced Filters
            </h3>
            {!isPerformancePage && (
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">
                Suggestion: Add a filter option when the stock list exceeds 10
                items
              </p>
            )}
          </div>
          <button
            onClick={() => {
              toggleFilterModal();
              setSelectedFilters(appliedFilters);
            }}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-5 space-y-3.5 sm:space-y-4 font-sans">
          {/* Risk Profile */}
          <div className="bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <h5 className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 bg-[#f4f5f8] dark:bg-[#30324e] uppercase tracking-wider">
              Risk Profile
            </h5>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-4 px-3 py-2.5 sm:px-4 sm:py-3">
              {riskOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center cursor-pointer text-xs sm:text-sm font-medium text-gray-900 dark:text-white"
                >
                  <input
                    type="checkbox"
                    checked={
                      selectedFilters.risk_profile?.includes(option.value) ||
                      false
                    }
                    onChange={(e) => {
                      const prev = selectedFilters.risk_profile || [];
                      const next = e.target.checked
                        ? [...prev, option.value]
                        : prev.filter((v) => v !== option.value);

                      setSelectedFilters((s) => ({ ...s, risk_profile: next }));
                    }}
                    className="form-checkbox h-4 w-4 text-blue-500"
                  />
                  <span className="ml-2">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Stock Filters */}
          <div className="bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-visible">
            <h5 className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 bg-[#f4f5f8] dark:bg-[#30324e] uppercase tracking-wider rounded-t-lg">
              Filter Stock By
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-3 py-2.5 sm:px-4 sm:py-3">
              <MultiSelect
                label="Sector"
                options={filtersData.sectors || []}
                values={selectedFilters.sector}
                onChange={(vals) =>
                  setSelectedFilters((prev) => ({ ...prev, sector: vals }))
                }
                placeholder="Select Sector"
              />
              <MultiSelect
                label="Segment"
                options={filtersData.segments || []}
                values={selectedFilters.segment}
                onChange={(vals) =>
                  setSelectedFilters((prev) => ({ ...prev, segment: vals }))
                }
                placeholder="Select Segment"
              />
            </div>
          </div>

          {/* Technical */}
          <div className="bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-visible">
            <h5 className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 bg-[#f4f5f8] dark:bg-[#30324e] uppercase tracking-wider rounded-t-lg">
              Technical Filters
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 px-3 py-2.5 sm:px-4 sm:py-3">
              <MultiSelect
                selecteAll={true}
                label="Near 200 SMA"
                options={filtersData.near_200_sma || []}
                values={selectedFilters.near200SMA}
                onChange={(vals) =>
                  setSelectedFilters((prev) => ({ ...prev, near200SMA: vals }))
                }
              />
              <MultiSelect
                label="Fibonacci Levels"
                options={filtersData.fibonacci_levels || []}
                values={selectedFilters.fibLevel}
                onChange={(vals) =>
                  setSelectedFilters((prev) => ({ ...prev, fibLevel: vals }))
                }
              />
            </div>
          </div>

          {/* Advanced */}
          <div className="bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <h5 className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 bg-[#f4f5f8] dark:bg-[#30324e] uppercase tracking-wider">
              Advanced Technical Filter
            </h5>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-4 px-3 py-2.5 sm:px-4 sm:py-3">
              <label className="flex items-center cursor-pointer text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                <input
                  type="checkbox"
                  checked={selectedFilters.machineLogic == "Buy"}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      machineLogic: e.target.checked ? "Buy" : "",
                    }))
                  }
                  className="form-checkbox h-4 w-4 text-blue-500"
                />
                <span className="ml-2">Machine Learning</span>
              </label>

              <label className="flex items-center cursor-pointer text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                <input
                  type="checkbox"
                  checked={selectedFilters.propLogic == "Buy"}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      propLogic: e.target.checked ? "Buy" : "",
                    }))
                  }
                  className="form-checkbox h-4 w-4 text-blue-500"
                />
                <span className="ml-2">Prop Logic</span>
              </label>

              <label className="flex items-center cursor-pointer text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                <input
                  type="checkbox"
                  checked={selectedFilters.abovesma20 || false}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      abovesma20: e.target.checked ? true : undefined,
                    }))
                  }
                  className="form-checkbox h-4 w-4 text-blue-500"
                />
                <span className="ml-2">Above 20SMA</span>
              </label>

              <label className="flex items-center cursor-pointer text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                <input
                  type="checkbox"
                  checked={selectedFilters.orderBlock || false}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      orderBlock: e.target.checked ? true : undefined,
                    }))
                  }
                  className="form-checkbox h-4 w-4 text-blue-500"
                />
                <span className="ml-2">Order Block</span>
              </label>

              <label className="flex items-center cursor-pointer text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                <input
                  type="checkbox"
                  checked={selectedFilters.hotFlame || false}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      hotFlame: e.target.checked ? true : undefined,
                    }))
                  }
                  className="form-checkbox h-4 w-4 text-blue-500"
                />
                <span className="ml-2">Hot Stock</span>
              </label>

              <label className="flex items-center cursor-pointer text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                <input
                  type="checkbox"
                  checked={selectedFilters.highvolume || false}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      highvolume: e.target.checked ? true : undefined,
                    }))
                  }
                  className="form-checkbox h-4 w-4 text-blue-500"
                />
                <span className="ml-2">High Volume</span>
              </label>

              <div className="relative flex items-center group">
                <label
                  className={`flex items-center gap-2 ${!hasCustomUniverseAccess ? "cursor-not-allowed opacity-50" : "cursor-pointer"} text-xs sm:text-sm font-medium`}
                >
                  <input
                    type="checkbox"
                    checked={selectedFilters.customUniverse || false}
                    disabled={!hasCustomUniverseAccess}
                    onChange={(e) =>
                      setSelectedFilters((prev) => ({
                        ...prev,
                        customUniverse: e.target.checked ? true : undefined,
                      }))
                    }
                    className="form-checkbox h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-200"
                  />
                  <span
                    className={
                      !hasCustomUniverseAccess
                        ? "text-gray-400"
                        : "text-gray-900 dark:text-white"
                    }
                  >
                    Custom Universe
                  </span>
                </label>
                {!hasCustomUniverseAccess && (
                  <div className="invisible group-hover:visible absolute bottom-full mb-2 left-0 z-50 transition-all duration-200 ease-in-out opacity-0 group-hover:opacity-100">
                    <div className="bg-black text-white text-[12px] text-semibold px-2 py-1 rounded shadow-xl whitespace-nowrap tracking-wide flex items-center gap-1.5">
                      Upgrade to Nifty 750 to unlock
                    </div>
                    <div className="w-2 h-2 bg-black rotate-45 ml-4 -mt-1"></div>
                  </div>
                )}
              </div>
              <label className="flex items-center cursor-pointer text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                <input
                  type="checkbox"
                  checked={selectedFilters.qualityStocks || false}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      qualityStocks: e.target.checked ? true : undefined,
                    }))
                  }
                  className="form-checkbox h-4 w-4 text-blue-500"
                />
                <span className="ml-2">Quality Candles</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap sm:flex-nowrap justify-between items-center gap-2 p-3 sm:p-4 bg-gray-50 dark:bg-gray-800/70 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button
            onClick={() => resetAllFilters(selectedDate)}
            className="px-3 py-1.5 sm:px-4 sm:py-2 cursor-pointer text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            Reset All Filters
          </button>
          <div className="flex items-center space-x-2 sm:space-x-3 ml-auto">
            <button
              onClick={() => {
                toggleFilterModal();
                setSelectedFilters(appliedFilters);
              }}
              className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-md cursor-pointer text-xs sm:text-sm font-medium text-gray-700 border border-gray-300 dark:border-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => applyFilters(selectedDate)}
              className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium text-white transition-opacity bg-[#00A85F] cursor-pointer hover:opacity-90 dark:bg-gradient-to-t dark:from-[#066766] dark:to-[#0FA243]"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Filters);
